import { forwardRef } from "react";
import FbkCardShell from "./FbkCardShell";
import {
  defaultFooterLeft,
  BRACKET_ROUND_LABEL,
  BRACKET_ROUND_MATCH_COUNT,
  type CardFormat,
  type BracketCardContent,
  type BracketMatchup,
} from "./types";

interface BracketCardProps {
  format: CardFormat;
  content: BracketCardContent;
}

const FONT_BEBAS = "'Bebas Neue', 'Anton', sans-serif";
const FONT_INTER = "'Inter', system-ui, sans-serif";

/**
 * FBK-themed knockout bracket card. Two columns of matchups flanking a
 * central FBK shield (admin-replaceable), with subtle gold bracket lines
 * connecting each pair to the center.
 *
 * Layout per matchup:
 *   [home flag] [vs.] [away flag]
 * The two flags read as the participants, "vs." separates them, and the
 * row sits inside a soft pill so each matchup feels self-contained.
 */
const BracketCard = forwardRef<HTMLDivElement, BracketCardProps>(
  function BracketCard({ format, content }, ref) {
    const isStory = format === "story";
    const expectedCount = BRACKET_ROUND_MATCH_COUNT[content.round];

    // Pad the matchups to the expected count so we always render the full
    // bracket — empty slots become TBD placeholders rather than collapsing.
    const matchups: BracketMatchup[] = Array.from(
      { length: expectedCount },
      (_, i) => content.matchups[i] ?? { homeName: "TBD", awayName: "TBD" },
    );

    // Final = 1 match centered. Everything else splits evenly into two
    // columns of N/2 rows.
    const isFinal = expectedCount === 1;
    const perColumn = isFinal ? 1 : expectedCount / 2;
    const leftMatchups = isFinal ? [] : matchups.slice(0, perColumn);
    const rightMatchups = isFinal ? [] : matchups.slice(perColumn);

    return (
      <FbkCardShell
        ref={ref}
        format={format}
        footerLeft={content.footerLeft || defaultFooterLeft("BRACKET")}
        footerRight={content.hashtags || "#WC2026 · #FBK"}
        header={content.header}
        background={content.style?.background}
        customBaseGradient={
          // FBK majestic — deep emerald saturation, brighter middle so the
          // central shield reads, gold-tinted vignette at the corners.
          [
            "radial-gradient(ellipse 70% 50% at 50% 50%, rgba(255,215,0,0.10) 0%, transparent 65%)",
            "radial-gradient(ellipse 60% 40% at 50% 45%, #0D8C3C 0%, #0D5C2E 35%, #07321A 70%, #051F11 100%)",
          ].join(", ")
        }
        suppressBrandOverlays
        hideFooter={content.footer?.hide}
      >
        {/* Subtle bracket line texture — diagonal gold lines fan out from center */}
        <BracketLines isStory={isStory} round={content.round} />

        {/* Title block */}
        <div
          style={{
            position: "relative",
            zIndex: 3,
            textAlign: "center",
            marginTop: isStory ? 24 : 16,
            marginBottom: isStory ? 28 : 18,
          }}
        >
          <div
            style={{
              fontFamily: FONT_BEBAS,
              fontSize: isStory ? 92 : 72,
              fontWeight: 800,
              color: "#FFFFFF",
              letterSpacing: "0.04em",
              lineHeight: 1,
              textShadow: "0 4px 22px rgba(0,0,0,0.55)",
            }}
          >
            {content.title}
          </div>
          <div
            style={{
              marginTop: isStory ? 8 : 6,
              fontFamily: FONT_INTER,
              fontSize: isStory ? 24 : 18,
              fontWeight: 800,
              color: "#FFD700",
              letterSpacing: "0.4em",
              textTransform: "uppercase",
            }}
          >
            {content.subtitle}
          </div>
          <div
            style={{
              marginTop: isStory ? 14 : 10,
              display: "inline-block",
              padding: `${isStory ? 8 : 6}px ${isStory ? 18 : 14}px`,
              borderRadius: 9999,
              border: "1px solid rgba(255,215,0,0.4)",
              background: "rgba(255,215,0,0.12)",
              color: "#FFD700",
              fontFamily: FONT_INTER,
              fontSize: isStory ? 18 : 14,
              fontWeight: 800,
              letterSpacing: "0.3em",
              textTransform: "uppercase",
            }}
          >
            {BRACKET_ROUND_LABEL[content.round]}
          </div>
        </div>

        {/* Bracket body — two columns flanking the center image */}
        <div
          style={{
            position: "relative",
            zIndex: 3,
            flex: 1,
            display: "grid",
            gridTemplateColumns: isFinal
              ? "1fr"
              : `1fr ${isStory ? 220 : 160}px 1fr`,
            alignItems: "center",
            gap: isStory ? 18 : 12,
            minHeight: 0,
          }}
        >
          {/* Left column */}
          {!isFinal && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: gapForCount(perColumn, isStory),
                justifyContent: "center",
              }}
            >
              {leftMatchups.map((m, i) => (
                <MatchupRow key={`L${i}`} matchup={m} isStory={isStory} />
              ))}
            </div>
          )}

          {/* Center column — FBK shield (or admin override) */}
          {!isFinal && (
            <CenterEmblem
              imageUrl={content.centerImageUrl || "/logo.png"}
              isStory={isStory}
            />
          )}

          {/* Right column */}
          {!isFinal ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: gapForCount(perColumn, isStory),
                justifyContent: "center",
              }}
            >
              {rightMatchups.map((m, i) => (
                <MatchupRow key={`R${i}`} matchup={m} isStory={isStory} />
              ))}
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: isStory ? 40 : 28,
              }}
            >
              <CenterEmblem
                imageUrl={content.centerImageUrl || "/logo.png"}
                isStory={isStory}
                large
              />
              <MatchupRow matchup={matchups[0]} isStory={isStory} large />
            </div>
          )}
        </div>
      </FbkCardShell>
    );
  },
);

export default BracketCard;

// ─── Single matchup row ─────────────────────────────────────────────────────

function MatchupRow({
  matchup,
  isStory,
  large,
}: {
  matchup: BracketMatchup;
  isStory: boolean;
  large?: boolean;
}) {
  const flagSize = large
    ? isStory ? 200 : 160
    : isStory ? 96 : 72;
  const vsFontSize = large
    ? isStory ? 56 : 42
    : isStory ? 22 : 17;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: large ? (isStory ? 24 : 18) : isStory ? 10 : 8,
        padding: large
          ? `${isStory ? 22 : 16}px ${isStory ? 32 : 22}px`
          : `${isStory ? 10 : 7}px ${isStory ? 14 : 10}px`,
        borderRadius: 9999,
        background: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,215,0,0.22)",
        boxShadow: "0 6px 20px rgba(0,0,0,0.35)",
        backdropFilter: "blur(4px)",
      }}
    >
      <Flag url={matchup.homeFlagUrl} name={matchup.homeName} size={flagSize} />
      <div
        style={{
          fontFamily: FONT_INTER,
          fontSize: vsFontSize,
          fontWeight: 800,
          color: "rgba(255,255,255,0.78)",
          fontStyle: "italic",
          letterSpacing: "0.04em",
        }}
      >
        vs.
      </div>
      <Flag url={matchup.awayFlagUrl} name={matchup.awayName} size={flagSize} />
    </div>
  );
}

// ─── A single circular flag ─────────────────────────────────────────────────

function Flag({
  url,
  name,
  size,
}: {
  url?: string;
  name: string;
  size: number;
}) {
  if (!url) {
    // TBD placeholder — gold ring with the 3-letter code
    return (
      <div
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.08)",
          border: "2px dashed rgba(255,215,0,0.55)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: FONT_BEBAS,
          fontSize: size * 0.32,
          color: "rgba(255,215,0,0.85)",
          letterSpacing: "0.05em",
        }}
      >
        {name.slice(0, 3).toUpperCase()}
      </div>
    );
  }
  return (
    <img
      src={url}
      alt={name}
      crossOrigin={url.startsWith("data:") ? undefined : "anonymous"}
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        objectFit: "cover",
        border: "3px solid #FFFFFF",
        boxShadow:
          "0 0 0 2px rgba(255,215,0,0.55), 0 6px 16px rgba(0,0,0,0.45)",
        background: "#fff",
      }}
    />
  );
}

// ─── Center FBK shield / replaceable emblem ────────────────────────────────

function CenterEmblem({
  imageUrl,
  isStory,
  large,
}: {
  imageUrl: string;
  isStory: boolean;
  large?: boolean;
}) {
  const size = large
    ? isStory ? 380 : 280
    : isStory ? 200 : 150;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
      }}
    >
      {/* Gold radial halo behind the emblem */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          width: size * 1.8,
          height: size * 1.8,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255,215,0,0.45) 0%, rgba(255,215,0,0.18) 35%, transparent 70%)",
          filter: "blur(4px)",
        }}
      />
      <img
        src={imageUrl}
        alt="FBK"
        crossOrigin={
          imageUrl.startsWith("data:") || imageUrl.startsWith("/")
            ? undefined
            : "anonymous"
        }
        style={{
          width: size,
          height: size,
          objectFit: "contain",
          position: "relative",
          zIndex: 1,
          filter: "drop-shadow(0 12px 32px rgba(0,0,0,0.55))",
        }}
      />
    </div>
  );
}

// ─── Decorative bracket connector lines ────────────────────────────────────

function BracketLines({
  isStory,
  round,
}: {
  isStory: boolean;
  round: BracketCardContent["round"];
}) {
  // For Round of 32 the bracket has a lot of rows — denser lines look
  // crowded, so we keep them at low opacity. For Final we skip lines entirely.
  if (round === "FINAL") return null;
  const tileSize = isStory ? 140 : 100;
  // Simple horizontal bracket connector — a thin horizontal stub line.
  const svg = encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='${tileSize}' height='${tileSize}' viewBox='0 0 ${tileSize} ${tileSize}'>
      <g fill='none' stroke='#FFD700' stroke-width='1.5' stroke-linecap='round'>
        <line x1='0' y1='${tileSize / 2}' x2='${tileSize * 0.35}' y2='${tileSize / 2}'/>
      </g>
    </svg>`,
  );
  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 1,
        opacity: 0.04,
        pointerEvents: "none",
        backgroundImage: `url("data:image/svg+xml;utf8,${svg}")`,
        backgroundSize: `${tileSize}px ${tileSize}px`,
        backgroundRepeat: "repeat",
      }}
    />
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────

/** Vertical gap between matchup rows scales with row count to fill the column. */
function gapForCount(count: number, isStory: boolean): number {
  if (count <= 1) return 0;
  if (count <= 2) return isStory ? 36 : 24;
  if (count <= 4) return isStory ? 26 : 18;
  if (count <= 8) return isStory ? 16 : 11;
  return isStory ? 10 : 7; // R32 — tight pack
}
